import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api/client'

export interface SettlementRow {
  settlementId: string
  gateway: string
  gatewaySettlementId: string
  utr: string | null
  settledOn: string
  grossAmount: number
  feeAmount: number
  taxAmount: number
  netAmount: number
  lineCount: number
  matchedCount: number
  exceptionCount: number
  importedAt: string
}

export interface SettledPaymentRow {
  paymentId: string
  paymentRef: string
  loanAccountNo: string
  customerName: string | null
  amount: number
  status: string
  channel: string
  paidAt: string | null
  utr: string | null
  gatewayPaymentId: string | null
  empId: string | null
  agentName: string | null
}

/** UNKNOWN_AT_LEDGER | AMOUNT_MISMATCH | STATUS_DRIFT | UNSETTLED_AGING */
export interface ExceptionRow {
  exceptionId: string
  kind: string
  settlementId: string | null
  paymentId: string | null
  paymentRef: string | null
  loanAccountNo: string | null
  gatewayOrderId: string | null
  gatewayPaymentId: string | null
  utr: string | null
  gatewayAmount: number | null
  ledgerAmount: number | null
  detail: string | null
  status: string
  detectedAt: string
  resolvedAt: string | null
  resolutionNote: string | null
}

export interface ImportResult {
  settlementsImported: number
  alreadyKnown: number
  linesMatched: number
  exceptionsRaised: number
  agingFlagged: number
}

export interface DateRange {
  from?: string | undefined
  to?: string | undefined
}

export function useSettlements(range: DateRange) {
  return useQuery({
    queryKey: ['settlements', 'list', range],
    queryFn: async () => {
      const res = await api.get<SettlementRow[]>('/v1/settlements', { params: range })
      return res.data
    },
  })
}

/** Only fetched once a batch is expanded — most rows are never opened. */
export function useSettlementLines(settlementId: string | null) {
  return useQuery({
    queryKey: ['settlements', 'lines', settlementId],
    enabled: settlementId !== null,
    queryFn: async () => {
      const res = await api.get<SettledPaymentRow[]>(`/v1/settlements/${settlementId}/lines`)
      return res.data
    },
  })
}

export function useSettlementExceptions(status: string) {
  return useQuery({
    queryKey: ['settlements', 'exceptions', status],
    queryFn: async () => {
      const res = await api.get<ExceptionRow[]>('/v1/settlements/exceptions', {
        params: { status },
      })
      return res.data
    },
  })
}

export function useImportSettlements() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (range: DateRange) => {
      const res = await api.post<ImportResult>('/v1/settlements/import', null, { params: range })
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settlements'] })
    },
  })
}

export function useResolveException() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { exceptionId: string; status: string; note: string }) => {
      await api.post(`/v1/settlements/exceptions/${input.exceptionId}/resolve`, {
        status: input.status,
        note: input.note,
      })
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settlements'] })
    },
  })
}

/**
 * Authenticated download, so it goes through axios rather than an `<a href>`
 * — a plain anchor sends no bearer token and comes back 401.
 */
export async function downloadSettlementXlsx(row: SettlementRow): Promise<void> {
  const res = await api.get<Blob>(`/v1/settlements/${row.settlementId}/export.xlsx`, {
    responseType: 'blob',
  })
  const url = URL.createObjectURL(res.data)
  try {
    const a = document.createElement('a')
    a.href = url
    a.download = `graviton-settlement-${row.settledOn}.xlsx`
    a.click()
  } finally {
    URL.revokeObjectURL(url)
  }
}
